import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
    andAllWhere,
    andWhere,
    buildQuery,
    CombinedFilter,
    FilterFn,
    noop,
    Ops,
    orderBy,
    OrderVariant,
    orWhere,
    page,
    select,
    where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import { AbstractService } from "app/services/abstract.service";
import { environment as env } from "../../../../../../environments/environment.prod";
import { MaterialStockAttributes, OrderAttributes, OrderDetailAttributes } from "@albatrosdeveloper/ave-models-package";
import { IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";

const apiUrl = env.API_AVE_ORDER_REPORT_URL;
const moduleName = "order_report";

@Injectable({
    providedIn: "root",
})
export class OrderCustomerLogService extends AbstractService<OrderDetailAttributes, OrderDetailAttributes, OrderDetailAttributes> {
    /** The last filter used to load the list */
    currentFilter: CombinedFilter<OrderDetailAttributes>;

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    findByFilter(filter: CombinedFilter<OrderDetailAttributes>, options?: RequestOptions) {
        const API_URL: string =
            apiUrl +
            `api/${moduleName}/order_changes_withCount`;
        const headers: Record<string, any> = {};
        // see order-customer-export for more info about this header
        // ctx: stands for context
        if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
        return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, {
            filter: filter
        }, { headers });
    }

    /**
     * Pagination
     * @param options Pagination options
     * @returns Paginated data
     *
     * 📍 Used in:
     * - Reportes/Ventas/Modificaciones/de Pedidos (`order-customer-log.component.ts`)
     * - Reportes/Ventas/Modificaciones/de Pedidos (modal)
     */
    findAllWithCount(
        options: IPagination<OrderDetailAttributes>,
        dataToFilter: IFilters
    ): Observable<IPaginationResult<OrderDetailAttributes>> {

        // Build base filters function following the established pattern
        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<OrderDetailAttributes>[] => {
            // First filter determines object separation (where vs orWhere)
            const firstFilter: FilterFn<OrderDetailAttributes> = params.useOrWhere
                ? orWhere('documentAfterUpdate.type.code' as any, dataToFilter.type ? Ops.eq(dataToFilter.type) : Ops.not(null))
                : where('documentAfterUpdate.type.code' as any, dataToFilter.type ? Ops.eq(dataToFilter.type) : Ops.not(null));

            const filters: FilterFn<OrderDetailAttributes>[] = [firstFilter];

            // Add all conditional base filters
            if (dataToFilter.documentClass && dataToFilter.documentClass.length > 0) {
                filters.push(andWhere("documentAfterUpdate.class.code" as any, Ops.in(...dataToFilter.documentClass)));
            }

            if (dataToFilter.seller) {
                filters.push(andWhere("documentAfterUpdate.user.user_login.username" as any, Ops.eq(dataToFilter.seller)));
            }

            if (dataToFilter.soldTo && dataToFilter.soldTo.length > 0) {
                filters.push(andWhere('documentAfterUpdate.soldTo.id' as any, Ops.eq(dataToFilter.soldTo)));
            }

            if (dataToFilter.shipTo != null && dataToFilter.shipTo.length > 0) {
                filters.push(andWhere('documentAfterUpdate.shipTo.id' as any, Ops.eq(dataToFilter.shipTo)));
            }

            if (dataToFilter.statusERP && dataToFilter.statusERP.length > 0 && !dataToFilter.statusERP.includes(null)) {
                filters.push(andWhere('documentAfterUpdate.statusERP' as any, Ops.in(...dataToFilter.statusERP)));
            }

            if (dataToFilter.status && dataToFilter.status.length > 0 && !dataToFilter.status.includes(null)) {
                filters.push(andWhere('documentAfterUpdate.statusId' as any, Ops.in(...dataToFilter.status)));
            }

            if (dataToFilter.position) {
                filters.push(andWhere('detailsPositionsUpdated' as any, Ops.eq(dataToFilter.position)));
            }

            if (dataToFilter.serial) {
                filters.push(andWhere('documentAfterUpdate.serial' as any, Ops.eq(dataToFilter.serial)));
            }

            if (dataToFilter.typeCustomer && dataToFilter.typeCustomer.length > 0 && !dataToFilter.typeCustomer.includes(null)) {
                filters.push(andWhere('documentAfterUpdate.types.code' as any, Ops.in(...dataToFilter.typeCustomer)));
            }

            // Date range filters
            if (dataToFilter.startDate && dataToFilter.endDate) {
                filters.push(andWhere("createdAt" as any, Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)));
            }

            if (dataToFilter.startTrip && dataToFilter.endTrip) {
                filters.push(andWhere("documentAfterUpdate.trip.dateHourStart" as any, Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip)));
            }

            if (dataToFilter.end_startTrip && dataToFilter.end_endTrip) {
                filters.push(andWhere("documentAfterUpdate.trip.dateHourEnd" as any, Ops.btwnDates(dataToFilter.end_startTrip, dataToFilter.end_endTrip)));
            }

            if (dataToFilter.startDeliveryDate && dataToFilter.endDeliveryDate) {
                filters.push(andWhere("documentAfterUpdate.deliveryDateTime" as any, Ops.btwnDates(dataToFilter.startDeliveryDate, dataToFilter.endDeliveryDate)));
            }

            if (dataToFilter.pickerUpload_start && dataToFilter.pickerUpload_end) {
                filters.push(andWhere("documentAfterUpdate.loadDateTime" as any, Ops.btwnDates(dataToFilter.pickerUpload_start, dataToFilter.pickerUpload_end)));
            }

            // Special case: ordersToFilter (only when no search term)
            if (dataToFilter.ordersToFilter && dataToFilter.ordersToFilter.length > 0 && (!options.search || options.search.trim() === '')) {
                filters.push(andWhere("documentAfterUpdate.detail.material.code" as any, Ops.in(...dataToFilter.ordersToFilter)));
            }

            // Array filters
            if (dataToFilter.warehouseId && dataToFilter.warehouseId.length > 0) {
                filters.push(andWhere('documentAfterUpdate.warehouses.id' as any, Ops.in(...dataToFilter.warehouseId)));
            }

            if (dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0) {
                filters.push(andWhere('documentAfterUpdate.subRoute.id' as any, Ops.in(...dataToFilter.subRouteId)));
            }

            if (dataToFilter.materialId && dataToFilter.materialId.length > 0) {
                filters.push(andWhere('documentAfterUpdate.detail.material.code' as any, Ops.in(...dataToFilter.materialId)));
            }

            return filters;
        };

        // Check if search term exists
        const hasSearch = options.search && options.search.trim() !== '';

        // Build search query objects (each with base filters)
        // Object 1: Search by material name (uses where - first object)
        const searchByMaterialName: FilterFn<OrderDetailAttributes>[] = [
            ...buildBaseFilters({ useOrWhere: false }),
            hasSearch ? andWhere("documentAfterUpdate.detail.material.name" as any, Ops.like(options.search!, "i")) : noop()
        ];

        // Object 2: Search by material code (uses orWhere - new OR object)
        const searchByMaterialCode: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere('documentAfterUpdate.detail.material.code' as any, Ops.like(options.search!, "i"))
        ] : [];

        // Object 3: Search by shipTo external code
        const searchByShipToCode: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere('documentAfterUpdate.shipTo.externalKeys.code' as any, Ops.like(options.search!, "i"))
        ] : [];

        // Object 4: Search by shipTo name
        const searchByShipToName: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere('documentAfterUpdate.shipTo.name' as any, Ops.like(options.search!, "i"))
        ] : [];

        // Object 5: Search by subRoute name
        const searchBySubRouteName: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("documentAfterUpdate.subRoute.name" as any, Ops.like(options.search!, "i"))
        ] : [];

        // Object 6: Search by username
        const searchByUsername: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("documentAfterUpdate.user.user_login.username" as any, Ops.like(options.search!, "i"))
        ] : [];

        // Object 7: Search by serial
        const searchBySerial: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("documentAfterUpdate.serial" as any, Ops.like(options.search!, "i"))
        ] : [];

        // Assemble final query
        const query = buildQuery<OrderDetailAttributes>(
            // Spread all search query objects
            ...searchByMaterialName,
            ...searchByMaterialCode,
            ...searchByShipToCode,
            ...searchByShipToName,
            ...searchBySubRouteName,
            ...searchByUsername,
            ...searchBySerial,

            // Sorting and pagination
            orderBy(
                options.sort ?? "createdAt",
                options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        this.currentFilter = query;

        const API_URL: string = apiUrl + `api/${moduleName}/order_changes_withCount`;
        return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, {
            filter: query
        });
    }






}
